/**
 * École Nid Douillet - Analytics Dashboard Component
 * 
 * Comprehensive analytics dashboard with advanced reporting features
 */

import React, { useState, useEffect } from 'react';
import { 
  analyticsApi, 
  EnrollmentAnalytics, 
  FinancialAnalytics, 
  OperationalAnalytics,
  AnalyticsQueryParams 
} from '@/services/analytics.api';
import { 
  ChartBarIcon, 
  CurrencyDollarIcon, 
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  DocumentChartBarIcon,
  CalendarIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

interface AnalyticsDashboardProps {
  className?: string;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ className = '' }) => {
  
  // State management
  const [enrollmentData, setEnrollmentData] = useState<EnrollmentAnalytics | null>(null);
  const [financialData, setFinancialData] = useState<FinancialAnalytics | null>(null);
  const [operationalData, setOperationalData] = useState<OperationalAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'enrollment' | 'financial' | 'operational'>('enrollment');
  
  // Filter states
  const [dateRange, setDateRange] = useState({
    from: format(startOfMonth(subMonths(new Date(), 5)), 'yyyy-MM-dd'),
    to: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [timeframe, setTimeframe] = useState<'monthly' | 'weekly' | 'daily'>('monthly');
  const [academicYearId, setAcademicYearId] = useState<number | undefined>();

  // Load analytics data
  useEffect(() => {
    const loadAnalyticsData = async () => {
      try {
        setLoading(true);
        setError(null);

        const params: AnalyticsQueryParams = {
          date_from: dateRange.from,
          date_to: dateRange.to,
          timeframe,
          academic_year_id: academicYearId,
          include_projections: true
        };

        const [enrollmentResponse, financialResponse, operationalResponse] = await Promise.all([
          analyticsApi.getEnrollmentAnalytics(params),
          analyticsApi.getFinancialAnalytics(params),
          analyticsApi.getOperationalAnalytics(params)
        ]);

        if (enrollmentResponse.success && enrollmentResponse.data) {
          setEnrollmentData(enrollmentResponse.data);
        }

        if (financialResponse.success && financialResponse.data) {
          setFinancialData(financialResponse.data);
        }

        if (operationalResponse.success && operationalResponse.data) {
          setOperationalData(operationalResponse.data);
        }

        if (!enrollmentResponse.success && !financialResponse.success && !operationalResponse.success) {
          setError('Erreur lors du chargement des données analytiques');
        }

      } catch (err) {
        console.error('Analytics loading error:', err);
        setError('Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    };

    loadAnalyticsData();
  }, [dateRange, timeframe, academicYearId]);

  // Generate comprehensive report
  const handleGenerateReport = async () => {
    try {
      const response = await analyticsApi.generateComprehensiveReport({
        date_from: dateRange.from,
        date_to: dateRange.to,
        academic_year_id: academicYearId,
        format_type: 'json'
      });

      if (response.success) {
        // Create downloadable file
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        
        const exportFileDefaultName = `rapport-analytique-${format(new Date(), 'yyyy-MM-dd')}.json`;
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      } else {
        setError(response.message || 'Erreur lors de la génération du rapport');
      }
    } catch (err) {
      console.error('Report generation error:', err);
      setError('Erreur lors de la génération du rapport');
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD'
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };



  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <ChartBarIcon className="h-8 w-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                Tableau de Bord Analytique
              </h1>
              <p className="text-neutral-600">
                Analyses avancées et rapports détaillés
              </p>
            </div>
          </div>
          
          <button
            onClick={handleGenerateReport}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Générer Rapport
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Date de début
            </label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Date de fin
            </label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Période
            </label>
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value as 'monthly' | 'weekly' | 'daily')}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="daily">Quotidien</option>
              <option value="weekly">Hebdomadaire</option>
              <option value="monthly">Mensuel</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Année académique
            </label>
            <select
              value={academicYearId || ''}
              onChange={(e) => setAcademicYearId(e.target.value ? parseInt(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Toutes les années</option>
              <option value="1">2024-2025</option>
              <option value="2">2023-2024</option>
            </select>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-neutral-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('enrollment')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'enrollment'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <UserGroupIcon className="h-4 w-4 mr-2" />
            Inscriptions
          </button>
          <button
            onClick={() => setActiveTab('financial')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'financial'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <CurrencyDollarIcon className="h-4 w-4 mr-2" />
            Financier
          </button>
          <button
            onClick={() => setActiveTab('operational')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'operational'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <DocumentChartBarIcon className="h-4 w-4 mr-2" />
            Opérationnel
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Content */}
      {activeTab === 'enrollment' && enrollmentData && (
        <div className="space-y-6">
          {/* Enrollment Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Total Enfants</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {enrollmentData.enrollment_stats.total_children}
                  </p>
                </div>
                <UserGroupIcon className="h-8 w-8 text-blue-500" />
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {enrollmentData.enrollment_stats.recent_enrollments} nouvelles inscriptions ce mois
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Âge Moyen</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {enrollmentData.enrollment_stats.average_age.toFixed(1)} ans
                  </p>
                </div>
                <CalendarIcon className="h-8 w-8 text-green-500" />
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                Répartition équilibrée par âge
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Garçons</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {enrollmentData.enrollment_stats.male_count}
                  </p>
                </div>
                <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold">♂</span>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {formatPercentage((enrollmentData.enrollment_stats.male_count / enrollmentData.enrollment_stats.total_children) * 100)}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Filles</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {enrollmentData.enrollment_stats.female_count}
                  </p>
                </div>
                <div className="h-8 w-8 bg-pink-100 rounded-full flex items-center justify-center">
                  <span className="text-pink-600 font-bold">♀</span>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {formatPercentage((enrollmentData.enrollment_stats.female_count / enrollmentData.enrollment_stats.total_children) * 100)}
              </p>
            </div>
          </div>

          {/* Class Distribution */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">
              Répartition par Classe
            </h3>
            <div className="space-y-4">
              {enrollmentData.class_distribution.map((classLevel, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-neutral-900">{classLevel.level_name}</h4>
                    <p className="text-sm text-neutral-600">
                      {classLevel.student_count} enfants • Âge moyen: {classLevel.average_age} ans
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-neutral-900">
                      {formatPercentage(classLevel.occupancy_rate)}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {classLevel.student_count}/{classLevel.capacity}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Enrollment Trends Chart Placeholder */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">
              Tendances d'Inscription
            </h3>
            <div className="h-64 bg-neutral-50 rounded-lg flex items-center justify-center">
              <p className="text-neutral-500">Graphique des tendances d'inscription</p>
              <p className="text-sm text-neutral-400 ml-2">(Intégration Chart.js à venir)</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'financial' && financialData && (
        <div className="space-y-6">
          {/* Financial Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Revenus Total</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(financialData.financial_summary.total_revenue)}
                  </p>
                </div>
                <CurrencyDollarIcon className="h-8 w-8 text-green-500" />
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {financialData.revenue_stats.completed_payments} paiements complétés
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Dépenses</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(financialData.financial_summary.total_expenses)}
                  </p>
                </div>
                <ArrowTrendingDownIcon className="h-8 w-8 text-red-500" />
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {financialData.expense_stats.total_expense_records} enregistrements
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Bénéfice Net</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(financialData.financial_summary.net_profit)}
                  </p>
                </div>
                <ArrowTrendingUpIcon className="h-8 w-8 text-blue-500" />
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                Marge: {formatPercentage(financialData.financial_summary.profit_margin)}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">En Retard</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(financialData.outstanding_payments.overdue_amount)}
                  </p>
                </div>
                <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-bold">!</span>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {financialData.outstanding_payments.overdue_count} paiements en retard
              </p>
            </div>
          </div>

          {/* Revenue Trends Chart Placeholder */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">
              Tendances de Revenus
            </h3>
            <div className="h-64 bg-neutral-50 rounded-lg flex items-center justify-center">
              <p className="text-neutral-500">Graphique des tendances de revenus</p>
              <p className="text-sm text-neutral-400 ml-2">(Intégration Chart.js à venir)</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'operational' && operationalData && (
        <div className="space-y-6">
          {/* Operational Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Parents Total</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {operationalData.parent_engagement.total_parents}
                  </p>
                </div>
                <UserGroupIcon className="h-8 w-8 text-purple-500" />
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {operationalData.parent_engagement.avg_children_per_parent.toFixed(1)} enfants/parent en moyenne
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Taux de Livraison</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {formatPercentage(operationalData.notification_stats.delivery_rate)}
                  </p>
                </div>
                <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {operationalData.notification_stats.delivered_count} notifications livrées
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Préférence Email</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {operationalData.parent_engagement.email_preferred}
                  </p>
                </div>
                <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold">@</span>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {formatPercentage((operationalData.parent_engagement.email_preferred / operationalData.parent_engagement.total_parents) * 100)}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Préférence SMS</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {operationalData.parent_engagement.sms_preferred}
                  </p>
                </div>
                <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 font-bold">📱</span>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {formatPercentage((operationalData.parent_engagement.sms_preferred / operationalData.parent_engagement.total_parents) * 100)}
              </p>
            </div>
          </div>

          {/* Care Services Utilization */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">
              Utilisation des Services de Garde
            </h3>
            <div className="space-y-4">
              {operationalData.care_services_utilization.map((service, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-neutral-900">{service.service_name}</h4>
                    <p className="text-sm text-neutral-600">
                      {service.current_enrollment} inscrits • {formatCurrency(service.monthly_fee)}/mois
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-neutral-900">
                      {formatPercentage(service.utilization_rate)}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {service.current_enrollment}/{service.capacity}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
